import React, { useState } from 'react';
import WetlandRatingSelect from './WetlandRatingSelect';
import './SpeciesEditForm.css';

const ROOT_TYPES = ['', 'fibrous', 'taproot', 'rhizomatous', 'stoloniferous'];
const ROOT_DEPTHS = ['', 'shallow', 'medium', 'deep'];
const SPREAD_RATES = ['', 'slow', 'moderate', 'aggressive'];
const GROUND_COVER = ['', 'sparse', 'moderate', 'dense'];
const FLOOD_TOLERANCES = ['', 'none', 'brief', 'moderate', 'extended', 'permanent'];
const TIDAL_TOLERANCES = ['', 'none', 'occasional splash', 'regular tidal', 'brackish submersion'];
const DROUGHT_TOLERANCES = ['', 'none', 'moderate', 'high'];
const ZONE_POSITIONS = ['', 'upland', 'transition', 'ponding zone', 'overflow', 'toe', 'bank', 'top of bank', 'floodplain'];

const APPLICATION_OPTIONS = [
  'stream bank',
  'rain garden',
  'slope stabilization',
  'bioswale',
  'living shoreline',
  'riparian buffer',
  'stormwater pond',
  'wetland restoration',
];

function SpeciesEditForm({ species, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    commonName: species?.commonName || '',
    scientificName: species?.scientificName || '',
    agcpRating: species?.agcpRating || null,
    empRating: species?.empRating || null,
    lightRequirements: species?.lightRequirements || '',
    moistureRequirements: species?.moistureRequirements || '',
    soilRequirements: species?.soilRequirements || '',
    bloomSeason: species?.bloomSeason || '',
    bloomColor: species?.bloomColor || '',
    saltTolerance: species?.saltTolerance || null,
    // New fields
    rootType: species?.rootType || '',
    rootDepth: species?.rootDepth || '',
    spreadRate: species?.spreadRate || '',
    groundCoverDensity: species?.groundCoverDensity || '',
    floodTolerance: species?.floodTolerance || '',
    tidalTolerance: species?.tidalTolerance || '',
    droughtTolerance: species?.droughtTolerance || '',
    applications: species?.applications || [],
    zonePosition: species?.zonePosition || '',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleApplication = (app) => {
    setFormData((prev) => {
      const apps = prev.applications.includes(app)
        ? prev.applications.filter(a => a !== app)
        : [...prev.applications, app];
      return { ...prev, applications: apps };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.commonName.trim() || !formData.scientificName.trim()) return;
    onSubmit(formData);
  };

  return (
    <form className="species-edit-form" onSubmit={handleSubmit} noValidate>
      <h2 className="species-edit-form__title">Edit Species</h2>

      <div className="species-edit-form__field">
        <label htmlFor="sp-common">Common Name *</label>
        <input id="sp-common" type="text" value={formData.commonName} onChange={(e) => handleChange('commonName', e.target.value)} required />
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-scientific">Scientific Name *</label>
        <input id="sp-scientific" type="text" value={formData.scientificName} onChange={(e) => handleChange('scientificName', e.target.value)} required />
      </div>

      <WetlandRatingSelect id="sp-agcp" label="AGCP Rating" value={formData.agcpRating} onChange={(val) => handleChange('agcpRating', val)} />
      <WetlandRatingSelect id="sp-emp" label="EMP Rating" value={formData.empRating} onChange={(val) => handleChange('empRating', val)} />

      <div className="species-edit-form__field">
        <label htmlFor="sp-light">Light Requirements</label>
        <input id="sp-light" type="text" value={formData.lightRequirements} onChange={(e) => handleChange('lightRequirements', e.target.value)} />
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-moisture">Moisture Requirements</label>
        <input id="sp-moisture" type="text" value={formData.moistureRequirements} onChange={(e) => handleChange('moistureRequirements', e.target.value)} />
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-soil">Soil Requirements</label>
        <input id="sp-soil" type="text" value={formData.soilRequirements} onChange={(e) => handleChange('soilRequirements', e.target.value)} />
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-bloom-season">Bloom Season</label>
        <input id="sp-bloom-season" type="text" value={formData.bloomSeason} onChange={(e) => handleChange('bloomSeason', e.target.value)} />
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-bloom-color">Bloom Color</label>
        <input id="sp-bloom-color" type="text" value={formData.bloomColor} onChange={(e) => handleChange('bloomColor', e.target.value)} />
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-salt">Salt Tolerance (1-5) {formData.saltTolerance ? `: ${formData.saltTolerance}` : ''}</label>
        <input id="sp-salt" type="range" min="1" max="5" step="1" value={formData.saltTolerance || 3} onChange={(e) => handleChange('saltTolerance', parseInt(e.target.value, 10))} className="species-edit-form__slider" />
      </div>

      <h3 className="species-edit-form__section-title">Erosion & Stabilization</h3>

      <div className="species-edit-form__field">
        <label htmlFor="sp-root-type">Root Type</label>
        <select id="sp-root-type" value={formData.rootType} onChange={(e) => handleChange('rootType', e.target.value)}>
          {ROOT_TYPES.map(v => <option key={v} value={v}>{v || '— Select —'}</option>)}
        </select>
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-root-depth">Root Depth</label>
        <select id="sp-root-depth" value={formData.rootDepth} onChange={(e) => handleChange('rootDepth', e.target.value)}>
          {ROOT_DEPTHS.map(v => <option key={v} value={v}>{v || '— Select —'}</option>)}
        </select>
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-spread">Spread Rate</label>
        <select id="sp-spread" value={formData.spreadRate} onChange={(e) => handleChange('spreadRate', e.target.value)}>
          {SPREAD_RATES.map(v => <option key={v} value={v}>{v || '— Select —'}</option>)}
        </select>
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-cover">Ground Cover Density</label>
        <select id="sp-cover" value={formData.groundCoverDensity} onChange={(e) => handleChange('groundCoverDensity', e.target.value)}>
          {GROUND_COVER.map(v => <option key={v} value={v}>{v || '— Select —'}</option>)}
        </select>
      </div>

      <h3 className="species-edit-form__section-title">Water Tolerance</h3>

      <div className="species-edit-form__field">
        <label htmlFor="sp-flood">Flood Tolerance</label>
        <select id="sp-flood" value={formData.floodTolerance} onChange={(e) => handleChange('floodTolerance', e.target.value)}>
          {FLOOD_TOLERANCES.map(v => <option key={v} value={v}>{v || '— Select —'}</option>)}
        </select>
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-tidal">Tidal Tolerance</label>
        <select id="sp-tidal" value={formData.tidalTolerance} onChange={(e) => handleChange('tidalTolerance', e.target.value)}>
          {TIDAL_TOLERANCES.map(v => <option key={v} value={v}>{v || '— Select —'}</option>)}
        </select>
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-drought">Drought Tolerance</label>
        <select id="sp-drought" value={formData.droughtTolerance} onChange={(e) => handleChange('droughtTolerance', e.target.value)}>
          {DROUGHT_TOLERANCES.map(v => <option key={v} value={v}>{v || '— Select —'}</option>)}
        </select>
      </div>

      <div className="species-edit-form__field">
        <label htmlFor="sp-zone">Zone Position</label>
        <select id="sp-zone" value={formData.zonePosition} onChange={(e) => handleChange('zonePosition', e.target.value)}>
          {ZONE_POSITIONS.map(v => <option key={v} value={v}>{v || '— Select —'}</option>)}
        </select>
      </div>

      <h3 className="species-edit-form__section-title">Applications</h3>

      <div className="species-edit-form__apps">
        {APPLICATION_OPTIONS.map(app => (
          <label key={app} className="species-edit-form__app-tag">
            <input
              type="checkbox"
              checked={formData.applications.includes(app)}
              onChange={() => toggleApplication(app)}
            />
            {app}
          </label>
        ))}
      </div>

      <div className="species-edit-form__actions">
        <button type="submit" className="species-edit-form__save-btn">Save Species</button>
        <button type="button" className="species-edit-form__cancel-btn" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export default SpeciesEditForm;
